import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Plus, Loader2, FolderOpen, ChevronRight, LogOut, HelpCircle,
  Folder, Tablet, Box, Tag, Layers, Archive, Pencil, Trash2, Send, CheckCircle2, Clock, BookOpen,
  Users, FileText, LayoutGrid, Menu, X, UserPlus
} from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import OnboardingWizard from "@/components/OnboardingWizard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Icon mapping
const ICONS = {
  folder: Folder,
  tablet: Tablet,
  box: Box,
  tag: Tag,
  layers: Layers,
  archive: Archive,
};

const COLOR_OPTIONS = [
  { value: "#6366f1", label: "Indigo" },
  { value: "#8b5cf6", label: "Viola" },
  { value: "#ec4899", label: "Rosa" },
  { value: "#ef4444", label: "Rosso" },
  { value: "#f97316", label: "Arancione" },
  { value: "#eab308", label: "Giallo" },
  { value: "#22c55e", label: "Verde" },
  { value: "#14b8a6", label: "Teal" },
  { value: "#3b82f6", label: "Blu" },
  { value: "#64748b", label: "Grigio" },
];

const ICON_OPTIONS = [
  { value: "folder", label: "Cartella", icon: Folder },
  { value: "tablet", label: "Tablet", icon: Tablet },
  { value: "box", label: "Box", icon: Box },
  { value: "tag", label: "Tag", icon: Tag },
  { value: "layers", label: "Livelli", icon: Layers },
  { value: "archive", label: "Archivio", icon: Archive },
];

const generateSlug = (name) => {
  return name
    .toLowerCase()
    .replace(/[àáâãäå]/g, 'a')
    .replace(/[èéêë]/g, 'e')
    .replace(/[ìíîï]/g, 'i')
    .replace(/[òóôõö]/g, 'o')
    .replace(/[ùúûü]/g, 'u')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

export default function AdminCardListPage({ auth }) {
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [models, setModels] = useState([]);
  const [stats, setStats] = useState({ totalSends: 0, confirmed: 0, pending: 0, avgTime: 0, totalGuides: 0 });
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeSection, setActiveSection] = useState('cards'); // cards, logs, users
  
  // Send logs state
  const [sendLogs, setSendLogs] = useState([]);
  const [selectedCardFilter, setSelectedCardFilter] = useState('all');
  
  // Users state
  const [users, setUsers] = useState([]);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'user' });
  const [creatingUser, setCreatingUser] = useState(false);
  
  // Card dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    color: "#6366f1",
    icon: "folder"
  });
  
  // Onboarding wizard
  const [showWizard, setShowWizard] = useState(() => {
    const hasSeenWizard = localStorage.getItem('guideflow_hasSeenWizard');
    return !hasSeenWizard;
  });
  
  const closeWizard = () => {
    setShowWizard(false);
    localStorage.setItem('guideflow_hasSeenWizard', 'true');
  };

  // Fetch functions
  const fetchCards = async () => {
    try {
      const res = await fetch(`${API}/admin/cards`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      const data = await res.json();
      setCards(data);
    } catch (err) {
      toast.error("Errore nel caricamento delle card");
    }
  };

  const fetchModels = async () => {
    try {
      const res = await fetch(`${API}/admin/models`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      const data = await res.json();
      setModels(data);
      setStats(prev => ({ ...prev, totalGuides: data.length }));
    } catch (err) {
      console.error("Error fetching models:", err);
    }
  };

  const fetchStats = async () => {
    try {
      const logsRes = await fetch(`${API}/admin/guide-sends/logs?limit=500`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      const logs = await logsRes.json();
      
      if (!Array.isArray(logs)) return;
      
      setSendLogs(logs);
      
      const confirmed = logs.filter(l => l.confirmed).length;
      const pending = logs.filter(l => !l.confirmed).length;
      
      const confirmedLogs = logs.filter(l => l.confirmed && l.confirmed_at && l.created_at);
      let avgTime = 0;
      if (confirmedLogs.length > 0) {
        const totalMs = confirmedLogs.reduce((sum, log) => {
          const created = new Date(log.created_at).getTime();
          const confirmedAt = new Date(log.confirmed_at).getTime();
          return sum + (confirmedAt - created);
        }, 0);
        avgTime = Math.round(totalMs / confirmedLogs.length / 60000);
      }
      
      setStats(prev => ({
        ...prev,
        totalSends: logs.length,
        confirmed,
        pending,
        avgTime
      }));
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  useEffect(() => {
    Promise.all([fetchCards(), fetchModels(), fetchStats(), fetchUsers()])
      .finally(() => setLoading(false));
  }, [auth.token]);

  // Auto-refresh logs every 10s when Log Invii section is active
  useEffect(() => {
    if (activeSection !== 'logs') return;
    const interval = setInterval(() => {
      fetchStats();
    }, 10000);
    return () => clearInterval(interval);
  }, [activeSection, auth.token]);

  // Get model counts for cards
  const getCardModelCount = (card) => {
    if (card.is_system && card.slug === 'altro') {
      return models.filter(m => !m.card_id).length;
    }
    return models.filter(m => m.card_id === card.id).length;
  };

  // Filter logs by card
  const getFilteredLogs = () => {
    if (selectedCardFilter === 'all') return sendLogs;
    
    const card = cards.find(c => c.id === selectedCardFilter);
    if (!card) return sendLogs;
    
    // Get model IDs for this card
    let cardModelIds;
    if (card.is_system && card.slug === 'altro') {
      cardModelIds = models.filter(m => !m.card_id).map(m => m.id);
    } else {
      cardModelIds = models.filter(m => m.card_id === card.id).map(m => m.id);
    }
    
    // Get guide IDs for these models
    const guideIds = models
      .filter(m => cardModelIds.includes(m.id))
      .map(m => m.guide?.id)
      .filter(Boolean);
    
    return sendLogs.filter(log => guideIds.includes(log.guide_id));
  };

  // Card CRUD
  const openNewCardDialog = () => {
    setEditingCard(null);
    setFormData({ name: "", slug: "", description: "", color: "#6366f1", icon: "folder" });
    setDialogOpen(true);
  };

  const openEditDialog = (card, e) => {
    e?.stopPropagation();
    setEditingCard(card);
    setFormData({
      name: card.name,
      slug: card.slug,
      description: card.description || "",
      color: card.color || "#6366f1",
      icon: card.icon || "folder"
    });
    setDialogOpen(true);
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData(prev => ({
      ...prev,
      name,
      slug: editingCard ? prev.slug : generateSlug(name)
    }));
  };

  const handleSaveCard = async () => {
    if (!formData.name.trim() || !formData.slug.trim()) {
      toast.error("Nome e slug sono obbligatori");
      return;
    }

    setSaving(true);
    try {
      const url = editingCard ? `${API}/admin/cards/${editingCard.id}` : `${API}/admin/cards`;
      const res = await fetch(url, {
        method: editingCard ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.token}`
        },
        body: JSON.stringify(formData)
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Errore nel salvataggio");

      toast.success(editingCard ? "Card aggiornata!" : "Card creata!");
      setDialogOpen(false);
      fetchCards();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCard = async (card, e) => {
    e?.stopPropagation();
    if (card.is_system) {
      toast.error("La card di sistema non può essere eliminata");
      return;
    }
    if (!window.confirm(`Eliminare la card "${card.name}"?`)) return;

    try {
      const res = await fetch(`${API}/admin/cards/${card.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (!res.ok) throw new Error("Errore nell'eliminazione");
      toast.success("Card eliminata");
      fetchCards();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // User CRUD
  const handleCreateUser = async () => {
    if (!newUser.username || !newUser.password) {
      toast.error("Username e password sono obbligatori");
      return;
    }
    
    setCreatingUser(true);
    try {
      const res = await fetch(`${API}/admin/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth.token}`
        },
        body: JSON.stringify(newUser)
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Errore nella creazione");
      }
      
      toast.success("Utente creato!");
      setShowCreateUser(false);
      setNewUser({ username: '', password: '', role: 'user' });
      fetchUsers();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreatingUser(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (user.username === auth.user?.username) {
      toast.error("Non puoi eliminare te stesso");
      return;
    }
    if (!window.confirm(`Eliminare l'utente "${user.username}"?`)) return;

    try {
      const res = await fetch(`${API}/admin/users/${user.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      if (!res.ok) throw new Error("Errore nell'eliminazione");
      toast.success("Utente eliminato");
      fetchUsers();
    } catch (err) {
      toast.error(err.message);
    }
  };

  // Format helpers
  const formatTime = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    return `${Math.floor(hours / 24)}g`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatResolutionTime = (log) => {
    if (!log.confirmed || !log.confirmed_at || !log.created_at) return null;
    const ms = new Date(log.confirmed_at).getTime() - new Date(log.created_at).getTime();
    const mins = Math.round(ms / 60000);
    return formatTime(mins);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const filteredLogs = getFilteredLogs();

  return (
    <div className="min-h-screen bg-white">
      {/* Onboarding Wizard */}
      <OnboardingWizard open={showWizard} onClose={closeWizard} />
      
      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-40 bg-white border-b px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-primary" />
          <span className="font-bold">GuideFlow</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{auth.user?.username}</span>
          <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <Menu className="w-5 h-5" />
          </Button>
        </div>
      </header>
      
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      <div className="flex">
        {/* Sidebar - Hidden on mobile, visible on desktop */}
        <aside className={`
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'} 
          fixed lg:sticky top-0 lg:top-0 z-50 lg:z-auto
          w-64 lg:w-64 h-screen
          bg-white border-r flex flex-col transition-transform duration-300
        `}>
          {/* Sidebar Header */}
          <div className="p-4 border-b flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-primary" />
              <span className="font-bold">GuideFlow</span>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)} className="lg:hidden">
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Sidebar Menu */}
          <nav className="flex-1 p-2">
            <Button
              variant={activeSection === 'cards' ? 'secondary' : 'ghost'}
              className="w-full justify-start mb-1"
              onClick={() => { setActiveSection('cards'); setSidebarOpen(false); }}
            >
              <LayoutGrid className="w-4 h-4" />
              <span className="ml-2">Categorie</span>
            </Button>
            
            <Button
              variant={activeSection === 'logs' ? 'secondary' : 'ghost'}
              className="w-full justify-start mb-1"
              onClick={() => { setActiveSection('logs'); setSidebarOpen(false); }}
            >
              <FileText className="w-4 h-4" />
              <span className="ml-2">Log Invii</span>
            </Button>
          
          {auth.user?.role === 'admin' && (
            <>
              <Button
                variant={activeSection === 'users' ? 'secondary' : 'ghost'}
                className="w-full justify-start mb-1"
                onClick={() => { setActiveSection('users'); setSidebarOpen(false); }}
              >
                <Users className="w-4 h-4" />
                <span className="ml-2">Utenti</span>
              </Button>
              
              <Button
                variant={activeSection === 'cardManagement' ? 'secondary' : 'ghost'}
                className="w-full justify-start mb-1"
                onClick={() => { setActiveSection('cardManagement'); setSidebarOpen(false); }}
              >
                <FolderOpen className="w-4 h-4" />
                <span className="ml-2">Gestione Card</span>
              </Button>
            </>
          )}
        </nav>
        
        {/* Sidebar Footer */}
        <div className="p-2 border-t">
          <Button variant="ghost" className="w-full justify-start" onClick={() => setShowWizard(true)} data-testid="help-wizard-btn">
            <HelpCircle className="w-4 h-4" />
            <span className="ml-2">Aiuto</span>
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen lg:min-h-0">
        {/* Desktop Header - Hidden on mobile */}
        <header className="hidden lg:flex border-b bg-white px-6 py-4 items-center justify-between flex-shrink-0">
          <div>
            <h1 className="text-xl font-bold">
              {activeSection === 'cards' && 'Categorie'}
              {activeSection === 'logs' && 'Log Invii Guide'}
              {activeSection === 'users' && 'Gestione Utenti'}
              {activeSection === 'cardManagement' && 'Gestione Card'}
            </h1>
          </div>
          
          <div className="flex items-center gap-4">
            {/* KPI Summary */}
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-1">
                <Send className="w-4 h-4 text-blue-500" />
                <span className="font-semibold">{stats.totalSends}</span>
                <span className="text-muted-foreground">invii</span>
              </div>
              <div className="flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                <span className="font-semibold">{stats.confirmed}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4 text-orange-500" />
                <span className="font-semibold">{stats.pending}</span>
              </div>
            </div>
            
            <span className="text-sm text-muted-foreground">{auth.user?.username}</span>
            <Button variant="ghost" size="icon" onClick={() => { localStorage.clear(); window.location.href = '/login'; }}>
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto bg-gray-50">
          {/* CARDS SECTION */}
          {activeSection === 'cards' && (
            <div className="space-y-4 lg:space-y-6">
              {/* Mobile Title */}
              <div className="lg:hidden">
                <h1 className="text-xl font-bold mb-4">Categorie</h1>
              </div>
              
              {/* KPI Cards - Responsive grid */}
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-4">
                <Card className="bg-blue-50 border-blue-200">
                  <CardContent className="p-3 lg:p-4">
                    <div className="flex items-center gap-2 lg:gap-3">
                      <Send className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600" />
                      <div>
                        <p className="text-xl lg:text-2xl font-bold text-blue-700">{stats.totalSends}</p>
                        <p className="text-xs text-blue-600/70">Invii</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="p-3 lg:p-4">
                    <div className="flex items-center gap-2 lg:gap-3">
                      <CheckCircle2 className="w-4 h-4 lg:w-5 lg:h-5 text-green-600" />
                      <div>
                        <p className="text-xl lg:text-2xl font-bold text-green-700">{stats.confirmed}</p>
                        <p className="text-xs text-green-600/70">Confermati</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-orange-50 border-orange-200">
                  <CardContent className="p-3 lg:p-4">
                    <div className="flex items-center gap-2 lg:gap-3">
                      <Clock className="w-4 h-4 lg:w-5 lg:h-5 text-orange-600" />
                      <div>
                        <p className="text-xl lg:text-2xl font-bold text-orange-700">{stats.pending}</p>
                        <p className="text-xs text-orange-600/70">In Attesa</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="p-3 lg:p-4">
                    <div className="flex items-center gap-2 lg:gap-3">
                      <Clock className="w-4 h-4 lg:w-5 lg:h-5 text-purple-600" />
                      <div>
                        <p className="text-xl lg:text-2xl font-bold text-purple-700">{formatTime(stats.avgTime)}</p>
                        <p className="text-xs text-purple-600/70">Tempo Medio</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-indigo-50 border-indigo-200 col-span-2 lg:col-span-1">
                  <CardContent className="p-3 lg:p-4">
                    <div className="flex items-center gap-2 lg:gap-3">
                      <BookOpen className="w-4 h-4 lg:w-5 lg:h-5 text-indigo-600" />
                      <div>
                        <p className="text-xl lg:text-2xl font-bold text-indigo-700">{stats.totalGuides}</p>
                        <p className="text-xs text-indigo-600/70">Guide</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Cards Grid - Responsive */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
                {cards.map((card) => {
                  const IconComponent = ICONS[card.icon] || Folder;
                  const modelCount = getCardModelCount(card);
                  return (
                    <Card
                      key={card.id}
                      className="group cursor-pointer hover:shadow-lg transition-all hover:border-primary/30 bg-white"
                      onClick={() => navigate(`/admin/cards/${card.id}`)}
                    >
                      <CardHeader className="pb-2 p-4">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 lg:w-12 lg:h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ backgroundColor: card.color + '20' }}
                          >
                            <IconComponent className="w-5 h-5 lg:w-6 lg:h-6" style={{ color: card.color }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <CardTitle className="text-base lg:text-lg flex items-center gap-2">
                              <span className="truncate">{card.name}</span>
                              {card.is_system && <Badge variant="outline" className="text-xs flex-shrink-0">Sistema</Badge>}
                            </CardTitle>
                            <CardDescription className="font-mono text-xs truncate">{card.slug}</CardDescription>
                          </div>
                          <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                        </div>
                      </CardHeader>
                      <CardContent className="p-4 pt-0">
                        <Badge variant="secondary">{modelCount} {modelCount === 1 ? 'guida' : 'guide'}</Badge>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* LOGS SECTION */}
          {activeSection === 'logs' && (
            <div className="space-y-4">
              {/* Mobile Title */}
              <div className="lg:hidden">
                <h1 className="text-xl font-bold mb-4">Log Invii Guide</h1>
              </div>
              
              {/* Filter by Card */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
                <Label className="whitespace-nowrap">Filtra per Card:</Label>
                <Select value={selectedCardFilter} onValueChange={setSelectedCardFilter}>
                  <SelectTrigger className="w-full sm:w-[200px]">
                    <SelectValue placeholder="Tutte le card" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte le card</SelectItem>
                    {cards.map(card => (
                      <SelectItem key={card.id} value={card.id}>{card.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Badge variant="outline">{filteredLogs.length} log</Badge>
                <span className="flex items-center gap-1.5 text-xs text-green-600">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                  </span>
                  Live
                </span>
              </div>

              {/* Logs - Mobile Cards / Desktop Table */}
              <div className="lg:hidden space-y-3">
                {filteredLogs.map((log) => {
                  const model = models.find(m => m.guide?.id === log.guide_id);
                  const card = model?.card_id ? cards.find(c => c.id === model.card_id) : cards.find(c => c.is_system);
                  const resTime = formatResolutionTime(log);
                  return (
                    <Card key={log.id} className="bg-white">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-semibold">{log.customer_name}</p>
                            <p className="text-sm text-muted-foreground">{log.phone_number}</p>
                          </div>
                          {log.confirmed ? (
                            <Badge className="bg-green-100 text-green-700">Confermato</Badge>
                          ) : log.current_step && log.total_steps ? (
                            <div className="text-right space-y-1">
                              <Badge variant="outline" className="text-blue-600 border-blue-200">
                                Passo {log.current_step}/{log.total_steps}
                              </Badge>
                              <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden ml-auto">
                                <div
                                  className="h-full bg-blue-500 rounded-full"
                                  style={{ width: `${(log.current_step / log.total_steps) * 100}%` }}
                                />
                              </div>
                            </div>
                          ) : log.opened_at ? (
                            <Badge variant="outline" className="text-purple-600 border-purple-200">Aperto</Badge>
                          ) : (
                            <Badge variant="outline" className="text-orange-600">In attesa</Badge>
                          )}
                        </div>
                        <p className="text-sm mb-2">{log.guide_title || model?.guide?.title || '-'}</p>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>Inviato da: {log.sent_by || '-'}</span>
                          <span>{formatDate(log.created_at)}</span>
                        </div>
                        {log.opened_at && (
                          <p className="text-xs text-blue-600 mt-1">Aperto il: {formatDate(log.opened_at)}</p>
                        )}
                        {resTime && <p className="text-xs text-muted-foreground mt-1">Tempo: {resTime}</p>}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Desktop Table */}
              <Card className="hidden lg:block">
                <CardContent className="p-0">
                  <ScrollArea className="h-[500px]">
                    <table className="w-full">
                      <thead className="bg-muted/50 sticky top-0">
                        <tr>
                          <th className="text-left p-3 text-sm font-medium">Cliente</th>
                          <th className="text-left p-3 text-sm font-medium">Guida</th>
                          <th className="text-left p-3 text-sm font-medium">Card</th>
                          <th className="text-left p-3 text-sm font-medium">Inviato da</th>
                          <th className="text-left p-3 text-sm font-medium">Data Invio</th>
                          <th className="text-left p-3 text-sm font-medium">Aperto il</th>
                          <th className="text-left p-3 text-sm font-medium">Stato</th>
                          <th className="text-left p-3 text-sm font-medium">Tempo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLogs.map((log) => {
                          const model = models.find(m => m.guide?.id === log.guide_id);
                          const card = model?.card_id ? cards.find(c => c.id === model.card_id) : cards.find(c => c.is_system);
                          const resTime = formatResolutionTime(log);
                          
                          return (
                            <tr key={log.id} className="border-b hover:bg-muted/30">
                              <td className="p-3">
                                <div>
                                  <p className="font-medium">{log.customer_name || '-'}</p>
                                  <p className="text-xs text-muted-foreground">{log.phone_number || '-'}</p>
                                </div>
                              </td>
                              <td className="p-3 text-sm">{model?.name || log.guide_id?.slice(0, 8)}</td>
                              <td className="p-3">
                                {card && (
                                  <Badge variant="outline" style={{ borderColor: card.color + '50', color: card.color }}>
                                    {card.name}
                                  </Badge>
                                )}
                              </td>
                              <td className="p-3">
                                <span className="text-sm font-medium text-primary">
                                  {log.sent_by || '-'}
                                </span>
                              </td>
                              <td className="p-3 text-sm text-muted-foreground">{formatDate(log.created_at)}</td>
                              <td className="p-3 text-sm">
                                {log.opened_at ? (
                                  <span className="text-blue-600">{formatDate(log.opened_at)}</span>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </td>
                              <td className="p-3">
                                {log.confirmed ? (
                                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    Confermato
                                  </Badge>
                                ) : log.current_step && log.total_steps ? (
                                  <div className="space-y-1">
                                    <Badge variant="outline" className="text-blue-600 border-blue-200">
                                      Passo {log.current_step}/{log.total_steps}
                                    </Badge>
                                    <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                      <div
                                        className="h-full bg-blue-500 rounded-full transition-all"
                                        style={{ width: `${(log.current_step / log.total_steps) * 100}%` }}
                                      />
                                    </div>
                                  </div>
                                ) : log.opened_at ? (
                                  <Badge variant="outline" className="text-purple-600 border-purple-200">
                                    Aperto
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-orange-600">
                                    <Clock className="w-3 h-3 mr-1" />
                                    In attesa
                                  </Badge>
                                )}
                              </td>
                              <td className="p-3 text-sm">
                                {resTime && <Badge variant="secondary">{resTime}</Badge>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          )}

          {/* USERS SECTION */}
          {activeSection === 'users' && auth.user?.role === 'admin' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button onClick={() => setShowCreateUser(true)}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Nuovo Utente
                </Button>
              </div>

              <Card>
                <CardContent className="p-0">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 text-sm font-medium">Username</th>
                        <th className="text-left p-3 text-sm font-medium">Ruolo</th>
                        <th className="text-left p-3 text-sm font-medium">Creato</th>
                        <th className="text-left p-3 text-sm font-medium">Ultimo Accesso</th>
                        <th className="text-right p-3 text-sm font-medium">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user.id} className="border-b hover:bg-muted/30">
                          <td className="p-3 font-medium">{user.username}</td>
                          <td className="p-3">
                            <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                              {user.role || 'user'}
                            </Badge>
                          </td>
                          <td className="p-3 text-sm text-muted-foreground">{formatDate(user.created_at)}</td>
                          <td className="p-3 text-sm text-muted-foreground">{formatDate(user.last_login)}</td>
                          <td className="p-3 text-right">
                            {user.username !== auth.user?.username && (
                              <Button variant="ghost" size="icon" onClick={() => handleDeleteUser(user)}>
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>

              {/* Create User Dialog */}
              <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Nuovo Utente</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Username</Label>
                      <Input
                        value={newUser.username}
                        onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                        placeholder="username"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Password</Label>
                      <Input
                        type="password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                        placeholder="••••••••"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Ruolo</Label>
                      <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="user">User</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowCreateUser(false)}>Annulla</Button>
                    <Button onClick={handleCreateUser} disabled={creatingUser}>
                      {creatingUser && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      Crea
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}

          {/* CARD MANAGEMENT SECTION */}
          {activeSection === 'cardManagement' && auth.user?.role === 'admin' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <Button onClick={openNewCardDialog}>
                  <Plus className="w-4 h-4 mr-2" />
                  Nuova Card
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {cards.map((card) => {
                  const IconComponent = ICONS[card.icon] || Folder;
                  return (
                    <Card key={card.id} className="group">
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-10 h-10 rounded-lg flex items-center justify-center"
                              style={{ backgroundColor: card.color + '20' }}
                            >
                              <IconComponent className="w-5 h-5" style={{ color: card.color }} />
                            </div>
                            <div>
                              <CardTitle className="text-base">{card.name}</CardTitle>
                              <CardDescription className="font-mono text-xs">{card.slug}</CardDescription>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => openEditDialog(card, e)}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            {!card.is_system && (
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={(e) => handleDeleteCard(card, e)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">{card.description || 'Nessuna descrizione'}</p>
                        {card.is_system && <Badge className="mt-2">Sistema</Badge>}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </main>
      </div>
      </div>

      {/* Card Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCard ? "Modifica Card" : "Nuova Card"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input value={formData.name} onChange={handleNameChange} placeholder="Es: iPratico" />
            </div>
            <div className="space-y-2">
              <Label>Slug *</Label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                className="font-mono"
                disabled={editingCard?.is_system}
              />
            </div>
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} />
            </div>
            <div className="space-y-2">
              <Label>Colore</Label>
              <div className="flex flex-wrap gap-2">
                {COLOR_OPTIONS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    className={`w-8 h-8 rounded-full border-2 ${formData.color === color.value ? 'border-foreground scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: color.value }}
                    onClick={() => setFormData({ ...formData, color: color.value })}
                  />
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Icona</Label>
              <div className="flex flex-wrap gap-2">
                {ICON_OPTIONS.map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={`w-10 h-10 rounded-lg border-2 flex items-center justify-center ${formData.icon === option.value ? 'border-primary bg-primary/10' : 'border-border'}`}
                      onClick={() => setFormData({ ...formData, icon: option.value })}
                    >
                      <Icon className="w-5 h-5" />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleSaveCard} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingCard ? "Salva" : "Crea"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
